import axios from 'axios';
import { toast } from 'react-toastify';
import { LogoutApp } from './authActions';


/**
 * Get all reports details with pagination and server-side filtering
 * 
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @param {number|null} therapistId - Optional therapist ID to filter reports
 * @param {string|null} startDate - Optional start date (YYYY-MM-DD format) to filter reports
 * @param {string|null} endDate - Optional end date (YYYY-MM-DD format) to filter reports
 * 
 * API Endpoint:
 * GET /api/v1/reports?page={page}&size={size}&therapistId={therapistId}&startDate={startDate}&endDate={endDate}
 * 
 * Example requests:
 * - Without filter: GET /api/v1/reports?page=0&size=20
 * - With therapist filter: GET /api/v1/reports?page=0&size=20&therapistId=271
 * - With date range: GET /api/v1/reports?page=0&size=20&startDate=2024-01-01&endDate=2024-12-31
 * - With all filters: GET /api/v1/reports?page=0&size=20&therapistId=271&startDate=2024-01-01&endDate=2024-12-31
 * 
 * Response format:
 * {
 *   "content": [...],
 *   "totalElements": 150,  // Reflects filtered count when filters are provided
 *   "totalPages": 8,
 *   "number": 0,
 *   "size": 20
 * }
 */
export const getAllReportsDetails = (page, size, therapistId = null, startDate = null, endDate = null) => async (dispatch) => {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
        toast.error('Authentication token missing. Please log in.');
        return dispatch(LogoutApp());
    }

    try {
        // Build query parameters
        let queryParams = `page=${page}&size=${size}`;
        
        // Add therapistId to query string if provided
        if (therapistId !== null && therapistId !== undefined) {
            queryParams += `&therapistId=${therapistId}`;
        }
        
        // Add startDate to query string if provided
        if (startDate !== null && startDate !== undefined && startDate !== '') {
            queryParams += `&startDate=${encodeURIComponent(startDate)}`;
        }
        
        // Add endDate to query string if provided
        if (endDate !== null && endDate !== undefined && endDate !== '') {
            queryParams += `&endDate=${encodeURIComponent(endDate)}`;
        }
        
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v1/reports?${queryParams}`,
            {
                headers: { Authorization: `Bearer ${authToken}` },
            }
        );
        dispatch({ type: "ALL_REPORTS", payload: response.data });
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 401) {
            toast.error('Session Expired. Please log in again.');
            dispatch(LogoutApp());
        } else {
            toast.error('Failed to fetch reports.');
        }
        return null;
    }
};
export const deleteReportById = (reportId) => async (dispatch) => {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
      toast.error('Authentication token missing. Please log in.');
      return dispatch(LogoutApp());
    }
  
    try {
      // Make the DELETE request with the report ID
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/v1/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success('Report deleted successfully!');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error('Session Expired. Please log in again.');
        dispatch(LogoutApp());
      } else {
        toast.error('Failed to delete report.');
      }
    }
};

/**
 * Generate a new report
 * 
 * @param {number|null} therapistId - Optional therapist ID for report generation
 * @param {string|null} fromDate - Optional start date (YYYY-MM-DD format) for report generation
 * @param {string|null} toDate - Optional end date (YYYY-MM-DD format) for report generation
 * 
 * Request body will only include fields that are provided (not null/undefined/empty)
 * Example: { therapistId: 271, fromDate: "2024-01-01", toDate: "2024-12-31" }
 * Or: {} if no parameters provided
 */
export const generateReport = (therapistId = null, fromDate = null, toDate = null) => async (dispatch) => { 
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
        toast.error('Authentication token missing. Please log in.');
        return dispatch(LogoutApp());
    }
    try {
        // Build request body dynamically - only include fields that are provided
        const requestBody = {};
        if (therapistId !== null && therapistId !== undefined) {
            requestBody.therapistId = therapistId;
        }
        if (fromDate !== null && fromDate !== undefined && fromDate !== '') {
            requestBody.fromDate = fromDate;
        }
        if (toDate !== null && toDate !== undefined && toDate !== '') {
            requestBody.toDate = toDate;
        }
        
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/reports`, requestBody, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        if(response.data.success){
            toast.success(response.data.message);  
        }else{
            toast.error(response.data.message);  
        }
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 401) {
            toast.error('Session Expired. Please log in again.');
            dispatch(LogoutApp());
        } else {
            toast.error('Failed to generate report.');
        }
    }
};
export const sendReportEmail = (formData) => async (dispatch) => { 
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
        toast.error('Authentication token missing. Please log in.');
        return dispatch(LogoutApp());
    }
    try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/reports/file-delivery/send`,formData, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        if(response.data.success){
            toast.success(response.data.message);  
        }else{
            toast.error(response.data.message);  
        }
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 401) {
            toast.error('Session Expired. Please log in again.');
            dispatch(LogoutApp());
        } else {
            toast.error('Failed to send email.');
        }
    }
};
// Helper function to extract filename from Content-Disposition header
const getFilenameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition) return null;
    
    // Try to extract filename from Content-Disposition header
    // Format: attachment; filename="filename.xlsx"
    // First try: filename="filename.xlsx" (quoted)
    let filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (filenameMatch && filenameMatch[1]) {
        return filenameMatch[1];
    }
    
    // Second try: filename=filename.xlsx (unquoted)
    filenameMatch = contentDisposition.match(/filename=([^;,\s]+)/i);
    if (filenameMatch && filenameMatch[1]) {
        return filenameMatch[1];
    }
    
    // Third try: filename*=UTF-8''encoded-filename (RFC 5987)
    filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;,\s]+)/i);
    if (filenameMatch && filenameMatch[1]) {
        try {
            return decodeURIComponent(filenameMatch[1]);
        } catch (e) {
            return filenameMatch[1];
        }
    }
    
    return null;
};

export const getTherapistsList = () => async (dispatch) => {
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
        toast.error('Authentication token missing. Please log in.');
        return dispatch(LogoutApp());
    }

    try {
        const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/v1/reports/therapists`,
            {
                headers: { Authorization: `Bearer ${authToken}` },
            }
        );
        dispatch({ type: "THERAPISTS_LIST", payload: response.data });
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 401) {
            toast.error('Session Expired. Please log in again.');
            dispatch(LogoutApp());
        } else {
            toast.error('Failed to fetch therapists list.');
        }
        return null;
    }
};

export const downloadReportFile = (id) => async (dispatch) => { 
    const authToken = sessionStorage.getItem('auth_token');
    if (!authToken) {
        toast.error('Authentication token missing. Please log in.');
        return dispatch(LogoutApp());
    }

    try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/reports/file-delivery/download/${id}`, {
            headers: { Authorization: `Bearer ${authToken}` },
            responseType: "blob", 
        });
        
        // Axios normalizes headers to lowercase, so check lowercase first
        // Also check all header keys to find Content-Disposition
        let contentDisposition = response.headers['content-disposition'];
        if (!contentDisposition) {
            // Try to find it in all headers (case-insensitive search)
            const headerKeys = Object.keys(response.headers);
            const dispositionKey = headerKeys.find(key => key.toLowerCase() === 'content-disposition');
            if (dispositionKey) {
                contentDisposition = response.headers[dispositionKey];
            }
        }
        
        // Extract filename from Content-Disposition header
        // NOTE: For this to work, the backend must include 'Content-Disposition' 
        // in the Access-Control-Expose-Headers CORS configuration
        const filename = getFilenameFromContentDisposition(contentDisposition);
        
        return {
            blob: response.data,
            filename: filename,
            mimeType: response.headers['content-type'] || response.data.type
        };
    } catch (error) {
        if (error.response && error.response.status === 401) {
            toast.error('Session Expired. Please log in again.');
            dispatch(LogoutApp());
        } else {
            toast.error('Failed to download report.');
        }
        return null;
    }
};